import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import breakpoints from "@shared/theme/props/breakpoints";
import { Z_INDEX } from "@shared/theme/props/zIndexes";
import CartSummary from "@app-contexts/public/features/basket/components/cart-summary/CartSummary/CartSummary.web";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import { useTranslation } from "@shared/utils/internationalization";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Drawer from "@ui-kit/overlays/Drawer/Drawer.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import { useEmbeddedComponent } from "@shared/hooks/useEmbeddedComponent";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import useRedirect from "@shared/hooks/useRedirect";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import StaticAlert from "@ui-kit/feedback/alerts/StaticAlert/StaticAlert.web";
import { CheckoutLabel } from "./CheckoutLabel.web";

const { sm, lg } = breakpoints;

export interface CartSummaryFooterRef {
  open: () => void;
  close: () => void;
}

/**
 * Footer that opens a cart summary drawer or cart summary modal.
 * For mobile and tablet screen sizes.
 * Used on attraction event pages, assigned seating pages, and dynamic holds.
 */
export const CartSummaryFooter = forwardRef<
  CartSummaryFooterRef,
  {
    venue:
      | PublicMembershipGroupSerializer["venue"]
      | PublicBaseEventSerializer["venue"];
    openCartSummaryModal?: () => void;
    showOnTablet?: boolean;
  }
>(({ venue, openCartSummaryModal = undefined, showOnTablet = false }, ref) => {
  // Hooks
  const portalRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("common");
  const { redirect } = useRedirect();
  const { isExternalEmbedded } = useEmbeddedComponent();
  const { seatingServiceState, disableBestAvailableSeating } =
    useSeatingState();
  const { isBasketUpdating, itemGroups, isWaitlist } = usePublicBasket();
  const { getItemTypeInPendingCart } = useAssignedSeatingHelpers();
  const navigateToCheckout = () => redirect(PUBLIC_ROUTES.CHECKOUT.ROOT);
  const [isSmallerThanSm] = useMediaQuery(`(max-width: ${sm - 1}px)`);
  const [isTablet] = useMediaQuery(
    `(min-width: 0px) and (max-width: ${lg - 1}px)`
  );
  const { pendingCartPermissions, pendingCartPermissionsCount } =
    seatingServiceState;
  const {
    isOpen: isCartOpen,
    onOpen: onCartOpen,
    onClose: onCartClose,
  } = useDisclosure();

  // Helpers
  const isSmallerDevice = useMemo(
    () => isSmallerThanSm || (showOnTablet && isTablet),
    [isSmallerThanSm, showOnTablet, isTablet]
  );

  const showOnDesktop = !!openCartSummaryModal || (showOnTablet && isTablet);
  const basketHasItems = useMemo(
    () => (itemGroups?.length ?? 0) > 0,
    [itemGroups]
  );
  const disableCheckout =
    isBasketUpdating || !itemGroups?.length || pendingCartPermissionsCount > 0;

  const openDrawer = useCallback(() => {
    if (openCartSummaryModal && !isSmallerDevice) {
      openCartSummaryModal();
    } else if (isSmallerDevice) {
      onCartOpen();
    }
  }, [onCartOpen, openCartSummaryModal, isSmallerDevice]);

  useImperativeHandle(
    ref,
    () => ({
      open: openDrawer,
      close: onCartClose,
    }),
    [openDrawer, onCartClose]
  );

  // Effects
  useEffect(() => {
    if (isCartOpen && !isSmallerDevice) {
      onCartClose();
      return;
    }

    if (isCartOpen && !basketHasItems) {
      onCartClose();
    }
  }, [isSmallerDevice, isCartOpen, onCartClose, basketHasItems]);

  if (!disableBestAvailableSeating) {
    return null;
  }

  return (
    <>
      {/* Mobile banner that displays the amount of minimum purchase tickets selected */}
      {isSmallerThanSm && pendingCartPermissionsCount > 0 && (
        <Flex
          w="full"
          h="auto"
          p="xs"
          bottom="none"
          fontSize="xs"
          direction="row"
          position="sticky"
          bg="bg"
          color="fg"
          borderTop="1px solid"
          borderTopColor="border"
          zIndex="sticky"
          justifyContent="space-between"
        >
          <Flex alignItems="center">
            <Icon.DangerCircle boxSize="element.md" mr="2xs" />
            <Box>
              {/* TODO: Add element token for 150px (between element.8xl=128px and element.9xl=160px) */}
              {/* eslint-disable-next-line showpass-custom/no-hardcoded-style-values */}
              <Text maxWidth="150px" isTruncated>
                {getItemTypeInPendingCart()?.name}
              </Text>
            </Box>
          </Flex>
          <Text>{`${pendingCartPermissionsCount} ${t("of", {
            ns: "seating",
          })} ${pendingCartPermissions[0]?.minPurchaseLimit}`}</Text>
        </Flex>
      )}

      {basketHasItems && (
        <>
          {/* Portal used by the Drawer */}
          <Box
            ref={portalRef}
            // TODO: Fix portal z-index via proper component layering
            // eslint-disable-next-line showpass-custom/prefer-semantic-props
            css={{
              ".chakra-portal .chakra-modal__content-container": {
                zIndex: Z_INDEX.drawer,
              },
            }}
          />

          <Drawer.BaseDrawer
            placement="bottom"
            onClose={onCartClose}
            isOpen={isCartOpen}
            portalProps={{
              appendToParentPortal: false,
              containerRef: portalRef,
            }}
          >
            <Drawer.Overlay zIndex="drawerOverlay" />
            <Drawer.Content
              borderTopLeftRadius="sm"
              borderTopRightRadius="sm"
              maxHeight="100dvh"
              bg="bg"
            >
              <Drawer.CloseButton
                right="xs"
                top="sm"
                data-testid="cart-summary-footer-close-button"
              />
              <Drawer.Header borderTopLeftRadius="sm" borderTopRightRadius="sm">
                <Text as="h2">{t("Cart Summary")}</Text>
              </Drawer.Header>
              <Drawer.Body>
                <CartSummary isDrawer onCartClose={onCartClose} venue={venue} />
              </Drawer.Body>
              {isWaitlist && itemGroups?.length && (
                <StaticAlert
                  status="info"
                  title={t(
                    "Tickets will not be issued when joining the waitlist"
                  )}
                  size="sm"
                />
              )}
              <Drawer.Footer>
                <Flex
                  hidden={isExternalEmbedded}
                  direction="column"
                  width="100%"
                >
                  <Divider />
                  <Box padding="xs md">
                    <Flex direction="row" gap="md">
                      <Button
                        data-testid="cart-summary-footer-keep-shopping-button"
                        variant="outline"
                        colorPalette="primary"
                        flex={1}
                        minHeight="element.3xl"
                        onClick={onCartClose}
                        isLoading={isBasketUpdating}
                      >
                        {t("Keep shopping", { ns: "common" })}
                      </Button>
                      <Button
                        data-testid="cart-summary-footer-open-checkout-button"
                        variant="solid"
                        colorPalette="primary"
                        flex={1}
                        minHeight="element.3xl"
                        onClick={navigateToCheckout}
                        isLoading={isBasketUpdating}
                        disabled={disableCheckout}
                      >
                        <CheckoutLabel />
                      </Button>
                    </Flex>
                  </Box>
                </Flex>
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.BaseDrawer>

          <Flex
            w="full"
            h="auto"
            bottom="none"
            bg="bg"
            position="sticky"
            direction="column"
            borderTop="1px solid"
            borderTopColor="border"
            zIndex="sticky"
            padding="md"
            display={{ base: "flex", sm: showOnDesktop ? "flex" : "none" }}
          >
            <Flex
              w="full"
              m="auto"
              direction="row"
              maxWidth="container.content"
              alignContent="center"
              justifyContent="space-between"
              padding={{ base: "xs md", xs: "xs 3xl" }}
            >
              <Button.Ghost
                data-testid="cart-summary-footer-cart-button"
                gap="2xs"
                display="flex"
                onClick={openDrawer}
                color="primary.800"
                disabled={pendingCartPermissionsCount > 0}
                marginRight={
                  showOnDesktop && !isSmallerDevice ? "2xs" : undefined
                }
                marginLeft={showOnDesktop && !isSmallerDevice ? "auto" : "-xs"}
                padding="none"
              >
                {showOnDesktop && !isSmallerDevice && (
                  <Text fontWeight={700} fontSize="lg">
                    {t("Cart", { ns: "common" })}
                  </Text>
                )}
                {!isCartOpen && <Icon.ChevronDown boxSize="element.xl" />}
              </Button.Ghost>

              <Button
                data-testid="cart-summary-footer-checkout-button"
                hidden={isExternalEmbedded}
                colorPalette="primary"
                variant="solid"
                minHeight="element.3xl"
                onClick={navigateToCheckout}
                isLoading={isBasketUpdating}
                disabled={disableCheckout}
              >
                <CheckoutLabel />
              </Button>
            </Flex>
          </Flex>
        </>
      )}
    </>
  );
});

CartSummaryFooter.displayName = "CartSummaryFooter";

export default CartSummaryFooter;
